import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { agentAccessApi, type AgentAccessEntry } from "@/api/agent-access";
import { tenantUsersApi, type TenantUser } from "@/api/tenant-users";
import { tenantGroupsApi, type TenantGroup } from "@/api/tenant-groups";
import { queryKeys } from "@/lib/query-keys";
import {
  Shield,
  Users,
  UsersRound,
  Trash2,
  Search,
  Cloud,
  HardDrive,
  X,
  UserCheck,
  Plus,
} from "lucide-react";

interface Props {
  agentId: string;
}

export function AgentAccessTab({ agentId }: Props) {
  const { tenantId, isTenantAdmin } = useTenant();
  // Grants can only be created/revoked by tenant admins. Agent admins
  // see the list read-only (so they know who they're collaborating
  // with) but can't change membership.
  const isAdmin = isTenantAdmin;
  const queryClient = useQueryClient();
  const [showGrant, setShowGrant] = useState<"user" | "group" | null>(null);
  const [search, setSearch] = useState("");

  const { data: entries = [], isLoading } = useQuery({
    queryKey: queryKeys.agentAccess.byAgent(agentId),
    queryFn: () => agentAccessApi.list(tenantId, agentId),
  });

  // Always fetch users and groups so they're ready when the picker opens
  const { data: users = [] } = useQuery({
    queryKey: queryKeys.tenantUsers.byCompany(tenantId),
    queryFn: () => tenantUsersApi.list(tenantId),
  });

  const { data: groups = [] } = useQuery({
    queryKey: queryKeys.tenantGroups.byCompany(tenantId),
    queryFn: () => tenantGroupsApi.list(tenantId),
  });

  const grantMutation = useMutation({
    mutationFn: (data: { tenantUserId?: string; groupId?: string }) =>
      agentAccessApi.grant(tenantId, agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agentAccess.byAgent(agentId) });
      setShowGrant(null);
      setSearch("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (accessId: string) => agentAccessApi.revoke(tenantId, agentId, accessId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.agentAccess.byAgent(agentId) }),
  });

  const existingUserIds = new Set(entries.filter((e) => e.tenantUserId).map((e) => e.tenantUserId));
  const existingGroupIds = new Set(entries.filter((e) => e.groupId).map((e) => e.groupId));

  const filteredUsers = users.filter(
    (u) =>
      !existingUserIds.has(u.id) &&
      ((u.displayName?.toLowerCase() || "").includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())),
  );

  const filteredGroups = groups.filter(
    (g) =>
      !existingGroupIds.has(g.id) &&
      g.name.toLowerCase().includes(search.toLowerCase()),
  );

  const userEntries = entries.filter((e) => e.tenantUserId);
  const groupEntries = entries.filter((e) => e.groupId);

  return (
    // p-6 matches the Config tab's inset so the content isn't flush to the
    // panel edges; h-full + overflow-y-auto lets a long grant list scroll.
    <div className="h-full overflow-y-auto p-6 space-y-4">
      {/* Header with action buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-text-primary">Access Control</h3>
          <p className="text-xs text-text-muted mt-1">
            {entries.length === 0
              ? "No grants — only tenant admins can see this agent."
              : `${entries.length} grant${entries.length !== 1 ? "s" : ""} — these users/groups can access this agent. Tenant admins always have access.`}
          </p>
        </div>
        {isAdmin && !showGrant && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowGrant("group"); setSearch(""); }}
            >
              <UsersRound size={14} className="mr-1.5" />
              Add Group
            </Button>
            <Button
              size="sm"
              onClick={() => { setShowGrant("user"); setSearch(""); }}
            >
              <Users size={14} className="mr-1.5" />
              Add User
            </Button>
          </div>
        )}
      </div>

      {/* Grant Access Picker */}
      {showGrant && (
        <Card className="border-accent/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-sm">
              <span>
                {showGrant === "user" ? "Select a user to grant access" : "Select a group to grant access"}
              </span>
              <Button size="sm" variant="ghost" onClick={() => { setShowGrant(null); setSearch(""); }}>
                <X size={14} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder={`Search ${showGrant === "user" ? "users" : "groups"}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm"
                autoFocus
              />
            </div>

            {/* Toggle between user/group within the picker */}
            <div className="mb-3 flex gap-1 rounded-md bg-surface-hover p-1">
              <button
                className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  showGrant === "user" ? "bg-surface shadow-sm text-text-primary" : "text-text-muted"
                }`}
                onClick={() => { setShowGrant("user"); setSearch(""); }}
              >
                <Users size={12} className="mr-1 inline" />
                Users ({filteredUsers.length})
              </button>
              <button
                className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  showGrant === "group" ? "bg-surface shadow-sm text-text-primary" : "text-text-muted"
                }`}
                onClick={() => { setShowGrant("group"); setSearch(""); }}
              >
                <UsersRound size={12} className="mr-1 inline" />
                Groups ({filteredGroups.length})
              </button>
            </div>

            <div className="max-h-[250px] overflow-y-auto rounded-md border border-border">
              {showGrant === "user" ? (
                filteredUsers.length === 0 ? (
                  <p className="py-6 text-xs text-text-muted text-center">
                    {users.length === 0
                      ? "No users imported yet. Go to Users page to import from Entra."
                      : "All users already have access, or no matches found."}
                  </p>
                ) : (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      className="flex w-full items-center gap-3 border-b border-border last:border-0 px-3 py-2.5 text-left hover:bg-surface-hover text-sm transition-colors"
                      onClick={() => grantMutation.mutate({ tenantUserId: user.id })}
                      disabled={grantMutation.isPending}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 shrink-0">
                        <span className="text-xs font-medium text-accent">
                          {(user.displayName || user.email)[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-text-primary truncate">
                          {user.displayName || user.email}
                        </p>
                        <p className="text-xs text-text-muted truncate">{user.email}</p>
                      </div>
                      <Plus size={16} className="text-accent shrink-0" />
                    </button>
                  ))
                )
              ) : filteredGroups.length === 0 ? (
                <p className="py-6 text-xs text-text-muted text-center">
                  {groups.length === 0
                    ? "No groups yet. Go to Groups page to create or import from Entra."
                    : "All groups already have access, or no matches found."}
                </p>
              ) : (
                filteredGroups.map((group) => (
                  <button
                    key={group.id}
                    className="flex w-full items-center gap-3 border-b border-border last:border-0 px-3 py-2.5 text-left hover:bg-surface-hover text-sm transition-colors"
                    onClick={() => grantMutation.mutate({ groupId: group.id })}
                    disabled={grantMutation.isPending}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 shrink-0">
                      <UsersRound size={16} className="text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-primary">{group.name}</p>
                      <p className="text-xs text-text-muted">
                        {group.memberCount} member{group.memberCount !== 1 ? "s" : ""}
                        <span className="ml-1.5">
                          {group.source === "entra" ? "· Entra" : "· Local"}
                        </span>
                      </p>
                    </div>
                    <Plus size={16} className="text-accent shrink-0" />
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Access Rules */}
      {groupEntries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <UsersRound size={16} />
              Group Access ({groupEntries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1">
              {groupEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-surface-hover"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                      <UsersRound size={14} className="text-accent" />
                    </div>
                    <div>
                      <span className="text-sm font-medium">{entry.groupName}</span>
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {entry.groupSource === "entra" ? (
                          <><Cloud size={10} className="mr-1" />Entra</>
                        ) : (
                          <><HardDrive size={10} className="mr-1" />Local</>
                        )}
                      </Badge>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => revokeMutation.mutate(entry.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {userEntries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users size={16} />
              Individual User Access ({userEntries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-1">
              {userEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-surface-hover"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10">
                      <span className="text-xs font-medium text-accent">
                        {(entry.userName || entry.userEmail || "?")[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {entry.userName || entry.userEmail}
                      </p>
                      {entry.userName && (
                        <p className="text-xs text-text-muted">{entry.userEmail}</p>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => revokeMutation.mutate(entry.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State with action buttons */}
      {entries.length === 0 && !isLoading && !showGrant && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Shield size={36} className="mb-3 text-text-muted" />
            <p className="text-sm font-medium text-text-secondary">No grants yet</p>
            <p className="text-xs text-text-muted mt-1 max-w-xs">
              Only tenant admins can see this agent right now. Grant access
              to specific users or groups below — they'll be able to chat
              with the agent, and anyone assigned the <strong>agent admin</strong>{" "}
              role will be able to manage its settings.
            </p>
            {isAdmin && (
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowGrant("group"); setSearch(""); }}
                >
                  <UsersRound size={14} className="mr-1.5" />
                  Restrict to Group
                </Button>
                <Button
                  size="sm"
                  onClick={() => { setShowGrant("user"); setSearch(""); }}
                >
                  <Users size={14} className="mr-1.5" />
                  Restrict to User
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
